import { useEffect, useRef } from "react";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
  placeholder?: string;
}

// A minimal Gmail-compose-style WYSIWYG editor for email template bodies — a
// contentEditable box with a formatting toolbar, so the platform owner edits
// visually instead of writing raw HTML tags. No editor library: the format
// commands map straight onto the browser's contentEditable primitives.
export const RichTextEditor = ({
  value,
  onChange,
  readOnly,
  placeholder,
}: RichTextEditorProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastValue = useRef(value);

  // Sync external value changes (switching templates, Cancel) without
  // clobbering the cursor while the user is actively typing in this box.
  useEffect(() => {
    if (
      ref.current &&
      value !== lastValue.current &&
      document.activeElement !== ref.current
    ) {
      ref.current.innerHTML = value;
      lastValue.current = value;
    }
  }, [value]);

  const handleInput = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastValue.current = html;
    onChange(html);
  };

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    handleInput();
  };

  const handleLink = () => {
    const url = window.prompt("Link URL");
    if (url) exec("createLink", url);
  };

  // Keep focus/selection inside the editor when a toolbar button is clicked —
  // otherwise the mousedown steals focus before the click's execCommand runs.
  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className={`rte${readOnly ? " rte--readonly" : ""}`}>
      {!readOnly && (
        <div className="rte__toolbar">
          <button
            type="button"
            className="rte__btn"
            style={{ fontWeight: 700 }}
            onMouseDown={keepFocus}
            onClick={() => exec("bold")}
            title="Bold"
          >
            B
          </button>
          <button
            type="button"
            className="rte__btn"
            style={{ fontStyle: "italic" }}
            onMouseDown={keepFocus}
            onClick={() => exec("italic")}
            title="Italic"
          >
            I
          </button>
          <button
            type="button"
            className="rte__btn"
            style={{ textDecoration: "underline" }}
            onMouseDown={keepFocus}
            onClick={() => exec("underline")}
            title="Underline"
          >
            U
          </button>
          <span className="rte__divider" />
          <button
            type="button"
            className="rte__btn"
            onMouseDown={keepFocus}
            onClick={() => exec("insertUnorderedList")}
            title="Bulleted list"
          >
            • List
          </button>
          <button
            type="button"
            className="rte__btn"
            onMouseDown={keepFocus}
            onClick={() => exec("insertOrderedList")}
            title="Numbered list"
          >
            1. List
          </button>
          <span className="rte__divider" />
          <button
            type="button"
            className="rte__btn"
            onMouseDown={keepFocus}
            onClick={handleLink}
            title="Insert link"
          >
            Link
          </button>
          <button
            type="button"
            className="rte__btn"
            onMouseDown={keepFocus}
            onClick={() => exec("removeFormat")}
            title="Clear formatting"
          >
            Clear
          </button>
        </div>
      )}
      <div
        ref={ref}
        className="rte__body"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder}
      />
    </div>
  );
};
